import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CheckSquare, Plus, CheckCircle2, XCircle, Clock,
  Send, AlertTriangle, Pen
} from 'lucide-react';

const STATUS_COLORS = {
  not_started: 'bg-slate-100 text-slate-500', requested: 'bg-blue-100 text-blue-700',
  submitted: 'bg-purple-100 text-purple-700', under_review: 'bg-indigo-100 text-indigo-700',
  signed: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
  waived: 'bg-slate-100 text-slate-400',
};

const SIGNOFF_GROUPS_NEC = ['Inspection/Test', 'Defect Corrected', 'Completion Readiness', 'Completion Support', 'Post-Completion', 'Defects Certificate'];
const SIGNOFF_GROUPS_JBCC = ['Internal QA', 'Practical Completion Inspection', 'PC Support Pack', 'Works Completion', 'Final Completion', 'Handover Docs'];
const SIGNOFF_GROUPS_FIDIC = ['Taking-Over Inspection', 'Snag Close-Out', 'Taking-Over Support', 'DNP Close-Out', 'Performance Certificate'];

export default function CloseoutSignoffs({ projectId, settings }) {
  const [signoffs, setSignoffs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ signoff_type: '', signoff_group: '', required_role: '', required_company: '', mandatory_flag: true, due_date: '' });

  const family = settings?.contract_family || 'JBCC';
  const groups = family.startsWith('NEC') ? SIGNOFF_GROUPS_NEC : family === 'FIDIC' ? SIGNOFF_GROUPS_FIDIC : SIGNOFF_GROUPS_JBCC;

  const loadData = useCallback(async () => {
    try {
      const res = await api.closeout.listSignoffs(projectId);
      setSignoffs(res.data.items || []);
      setStats(res.data.stats || {});
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    if (!form.signoff_type.trim()) { toast.error('Sign-off type required'); return; }
    try {
      await api.closeout.createSignoff(projectId, form);
      toast.success('Sign-off requirement created');
      setShowCreate(false);
      setForm({ signoff_type: '', signoff_group: '', required_role: '', required_company: '', mandatory_flag: true, due_date: '' });
      loadData();
    } catch { toast.error('Failed to create'); }
  };

  const handleAction = async (signoffId, action) => {
    try {
      await api.closeout.signoffAction(signoffId, action);
      toast.success(`Sign-off ${action}`);
      loadData();
    } catch { toast.error('Action failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const grouped = {};
  signoffs.forEach(s => { const g = s.signoff_group || 'General'; grouped[g] = grouped[g] || []; grouped[g].push(s); });

  return (
    <div className="space-y-4" data-testid="closeout-signoffs">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          Sign-Offs <span className="ml-2 text-sm font-normal text-slate-400">({stats.total || 0} total, {stats.outstanding || 0} outstanding)</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowCreate(true)} data-testid="create-signoff-btn">
          <Plus className="h-4 w-4" /> Add Sign-Off
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{stats.total || 0}</p><p className="text-[10px] text-slate-500 uppercase">Total</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-600">{stats.signed || 0}</p><p className="text-[10px] text-slate-500 uppercase">Signed</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-600">{stats.outstanding || 0}</p><p className="text-[10px] text-slate-500 uppercase">Outstanding</p></CardContent></Card>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <Card><CardContent className="p-12 text-center"><CheckSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-lg font-medium text-slate-500">No sign-off requirements yet</p></CardContent></Card>
      ) : (
        Object.entries(grouped).map(([group, items]) => (
          <Card key={group}>
            <CardContent className="p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{group} ({items.length})</h3>
              <div className="space-y-1.5">
                {items.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 border border-slate-100" data-testid={`signoff-${s.id}`}>
                    <CheckSquare className={`h-4 w-4 flex-shrink-0 ${s.status === 'signed' ? 'text-green-500' : 'text-slate-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{s.signoff_type}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        {s.required_role && <span>Role: {s.required_role}</span>}
                        {s.required_company && <span>Company: {s.required_company}</span>}
                        {s.mandatory_flag && <Badge className="bg-red-50 text-red-600 text-[9px]">Mandatory</Badge>}
                      </div>
                    </div>
                    <Badge className={`text-[10px] ${STATUS_COLORS[s.status]}`}>{s.status?.replace(/_/g, ' ')}</Badge>
                    <div className="flex gap-1">
                      {s.status === 'not_started' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAction(s.id, 'requested')}>Request</Button>}
                      {s.status === 'requested' && <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleAction(s.id, 'signed')}><Pen className="h-3 w-3" />Sign</Button>}
                      {['not_started', 'requested'].includes(s.status) && <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={() => handleAction(s.id, 'waived')}>Waive</Button>}
                      {s.status === 'rejected' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAction(s.id, 'requested')}>Re-request</Button>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Add Sign-Off Requirement</DialogTitle>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Sign-Off Type *</Label><Input value={form.signoff_type} onChange={e => setForm(f => ({...f, signoff_type: e.target.value}))} placeholder="e.g. Electrical Test Sign-off" data-testid="signoff-type-input" /></div>
            <div><Label className="text-xs">Group</Label>
              <Select value={form.signoff_group} onValueChange={v => setForm(f => ({...f, signoff_group: v}))}>
                <SelectTrigger><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>{groups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Required Role</Label><Input value={form.required_role} onChange={e => setForm(f => ({...f, required_role: e.target.value}))} placeholder="e.g. Principal Agent" /></div>
              <div><Label className="text-xs">Required Company</Label><Input value={form.required_company} onChange={e => setForm(f => ({...f, required_company: e.target.value}))} placeholder="Company" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.mandatory_flag} onChange={e => setForm(f => ({...f, mandatory_flag: e.target.checked}))} className="rounded" /> Mandatory for completion</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleCreate} data-testid="create-signoff-submit"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
